import { apiFetch } from "@d12labs/client";
import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@d12labs/ui/select";
import { cn } from "@d12labs/utils";
import { useMutation } from "@tanstack/react-query";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/** A schema row merged with its live runtime state (from `/api/admin/settings`). */
export type SettingRowView = {
	key: string;
	label: string;
	help?: string;
	secret: boolean;
	restartRequired: boolean;
	floor: boolean;
	editable: boolean;
	source: "env" | "db" | "default";
	value: string | null;
	hasValue: boolean;
	placeholder?: string;
	/** Closed list of allowed values — renders a select instead of free text. */
	options?: { value: string; label: string }[];
};

/** env (lavender) / default (grey) / needs restart (amber) — driven by live state. */
function StatusChips({ source, restartRequired }: { source: string; restartRequired: boolean }) {
	return (
		<>
			{source === "env" ? <Badge tone="primary">env</Badge> : <Badge tone="subtle">default</Badge>}
			{restartRequired ? <Badge tone="warning">needs restart</Badge> : null}
		</>
	);
}

function ReadOnlyValue({ row }: { row: SettingRowView }) {
	const raw = row.secret ? (row.hasValue ? "••••••••••••" : "") : (row.value ?? "");
	// Options-backed rows show the operator label; an off-list env value shows raw.
	const label = row.options?.find((o) => o.value === raw)?.label;
	const display = label && label !== raw ? `${label} (${raw})` : raw;
	return (
		<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm lg:justify-end">
			<span
				className={cn(
					"tabular-nums",
					display ? "text-ink-muted" : "text-ink-tertiary",
					row.secret && "break-all font-mono",
				)}
			>
				{display || "not set"}
			</span>
			<span className="inline-flex items-center gap-1 whitespace-nowrap text-meta text-ink-tertiary">
				<Lock className="size-3" /> read-only
			</span>
		</div>
	);
}

function EditableControl({ row, onSaved }: { row: SettingRowView; onSaved: () => void }) {
	// For non-secrets the input is pre-filled with the effective value; for secrets
	// it always starts empty (the stored plaintext is never sent to the client —
	// the reveal toggle only shows what the user just typed).
	const [draft, setDraft] = useState<string | null>(null);
	const [reveal, setReveal] = useState(false);
	const display = draft ?? (row.secret ? "" : (row.value ?? ""));

	const save = useMutation({
		mutationFn: (value: string) =>
			apiFetch("/api/admin/settings", {
				method: "PUT",
				body: JSON.stringify({ key: row.key, value }),
			}),
		onSuccess: () => {
			setDraft(null);
			setReveal(false);
			toast.success(`${row.label} updated`);
			onSaved();
		},
	});

	const placeholder = row.secret
		? row.hasValue
			? "•••• (set — leave blank to keep)"
			: (row.placeholder ?? "not set")
		: (row.placeholder ?? "not set");

	const saveDisabled =
		save.isPending || (row.secret && display === "") || (!!row.options && display === "");

	return (
		<div className="flex w-full items-center gap-2">
			<div className="relative flex-1">
				{row.secret ? (
					<Lock className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-tertiary" />
				) : null}
				{row.options ? (
					// Radix Select forbids an empty-string item, so "unset" is the
					// no-selection state (placeholder) and Clear is the way back to default.
					<Select value={display} onValueChange={setDraft}>
						<SelectTrigger aria-label={row.label} className="w-full">
							<SelectValue placeholder={placeholder} />
						</SelectTrigger>
						<SelectContent>
							{row.options.map((o) => (
								<SelectItem key={o.value} value={o.value}>
									{o.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					<Input
						type={row.secret && !reveal ? "password" : "text"}
						value={display}
						placeholder={placeholder}
						onChange={(e) => setDraft(e.target.value)}
						className={cn(row.secret && "pl-8 pr-9")}
					/>
				)}
				{row.secret ? (
					<button
						type="button"
						onClick={() => setReveal((r) => !r)}
						title={reveal ? "Hide" : "Reveal"}
						aria-label={reveal ? "Hide value" : "Reveal value"}
						className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle transition-colors hover:text-ink"
					>
						{reveal ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
					</button>
				) : null}
			</div>
			<Button size="sm" disabled={saveDisabled} onClick={() => save.mutate(display)}>
				Save
			</Button>
			{row.hasValue && row.editable ? (
				<Button
					size="sm"
					variant="ghost"
					disabled={save.isPending}
					onClick={() => save.mutate("")}
					title="Clear the saved override"
				>
					Clear
				</Button>
			) : null}
		</div>
	);
}

export function SettingRow({
	row,
	last,
	onSaved,
}: {
	row: SettingRowView;
	last: boolean;
	onSaved: () => void;
}) {
	return (
		<div
			className={cn(
				"grid grid-cols-1 gap-x-8 gap-y-3 px-5 py-4 transition-colors hover:bg-surface-2/30 lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)]",
				!last && "border-b border-hairline",
			)}
		>
			{/* left: identity + help */}
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-body-sm font-medium text-foreground">{row.label}</span>
					<StatusChips source={row.source} restartRequired={row.restartRequired} />
				</div>
				<div className="mt-0.5 font-mono text-meta text-ink-tertiary">{row.key}</div>
				{row.help ? (
					<p className="mt-1.5 max-w-xl text-pretty text-body-sm leading-snug text-ink-subtle">
						{row.help}
					</p>
				) : null}
			</div>

			{/* right: control */}
			<div className="flex items-start lg:justify-end">
				{row.editable ? (
					<EditableControl row={row} onSaved={onSaved} />
				) : (
					<div className="lg:pt-1.5">
						<ReadOnlyValue row={row} />
					</div>
				)}
			</div>
		</div>
	);
}
